import React, { useState, useEffect } from 'react';

const API = '/api';

const socColor = s => s >= 80 ? '#4ade80' : s >= 40 ? '#60a5fa' : s >= 20 ? '#f59e0b' : '#f87171';
const socLabel = s => s >= 80 ? 'Carga completa' : s >= 40 ? 'Cargando' : s >= 20 ? 'Carga baja' : 'Crítico';

export default function Gestion() {
  const [chargers, setChargers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [liveChargers, setLiveChargers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCop, setFilterCop] = useState('');

  const fetchData = () => {
    Promise.all([
      fetch(`${API}/chargers`).then(r=>r.json()),
      fetch(`${API}/transactions`).then(r=>r.json()),
      fetch(`${API}/live`).then(r=>r.json()),
    ]).then(([c, t, l]) => {
      setChargers(c);
      setTxs(t);
      setLiveChargers(l);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 10000);
    return () => clearInterval(iv);
  }, []);

  // Buses cargando ahora — cruzar chargers en Charging con transacciones activas
  const busesActivos = chargers
    .filter(c => c.status === 'Charging' && liveChargers.includes(c.id))
    .filter(c => !filterCop || c.cop === filterCop)
    .map(c => {
      const txActiva = txs.find(t => t.cp_id === c.id && t.status === 'Active');
      const durMin = txActiva?.start_time
        ? Math.round((Date.now() - new Date(txActiva.start_time)) / 60000)
        : 0;
      return {
        cpId: c.id, cop: c.cop,
        soc: c.soc, power: c.power || 0,
        zonal: txActiva?.zonal || '—',
        vin: txActiva?.vin || '—',
        startTime: txActiva?.start_time,
        duracion: durMin,
        txId: txActiva?.tx_id,
      };
    });

  // Cargadores disponibles (en vivo pero sin bus)
  const disponibles = chargers
    .filter(c => c.status === 'Available' && liveChargers.includes(c.id))
    .filter(c => !filterCop || c.cop === filterCop);

  // Métricas
  const totalCargando = busesActivos.length;
  const potenciaTotal = busesActivos.reduce((a,b) => a + (b.power||0), 0).toFixed(1);
  const socPromedio = busesActivos.length > 0
    ? Math.round(busesActivos.filter(b=>b.soc!=null).reduce((a,b)=>a+(b.soc||0),0) / busesActivos.filter(b=>b.soc!=null).length)
    : 0;
  const criticos = busesActivos.filter(b => b.soc !== null && b.soc < 20).length;

  const durStr = m => m >= 60 ? `${Math.floor(m/60)}h ${m%60}m` : `${m}m`;

  if (loading) return <div style={{padding:'3rem',textAlign:'center',color:'var(--text3)'}}>Cargando gestión...</div>;

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem',flexWrap:'wrap',gap:'12px'}}>
        <div className="page-title" style={{margin:0}}>Gestión de carga en vivo</div>
        <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
          <select value={filterCop} onChange={e=>setFilterCop(e.target.value)}>
            <option value="">Todos los COP</option>
            <option value="Blanco y Negro">Blanco y Negro</option>
          </select>
          <span style={{fontSize:'12px',color:'var(--text4)'}}>Actualiza cada 10s</span>
        </div>
      </div>

      <div className="metrics">
        <div className="metric-card">
          <div className="metric-label">Buses cargando</div>
          <div className="metric-value" style={{color:'#60a5fa'}}>{totalCargando}</div>
          <div className="metric-sub">en este momento</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Potencia total</div>
          <div className="metric-value">{potenciaTotal} kW</div>
          <div className="metric-sub">demanda activa</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">SoC promedio</div>
          <div className="metric-value" style={{color:socColor(socPromedio)}}>{socPromedio}%</div>
          <div className="metric-sub">flota en carga</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Cargadores libres</div>
          <div className="metric-value" style={{color:'var(--accent)'}}>{disponibles.length}</div>
          <div className="metric-sub">disponibles en vivo</div>
        </div>
      </div>

      {criticos > 0 && (
        <div style={{background:'rgba(248,113,113,0.1)',border:'1px solid #f87171',borderRadius:'8px',padding:'10px 16px',marginBottom:'1.25rem',fontSize:'13px',color:'#f87171'}}>
          🚨 {criticos} bus{criticos>1?'es':''} con SoC crítico (&lt;20%) — requiere atención inmediata
        </div>
      )}

      {/* Buses cargando */}
      {busesActivos.length > 0 ? (
        <div className="card">
          <div className="card-title">Buses cargando ahora</div>
          <table>
            <thead>
              <tr>
                <th>Cargador</th>
                <th>COP</th>
                <th>Bus / Zonal</th>
                <th>SoC</th>
                <th>Barra</th>
                <th>Potencia</th>
                <th>Tiempo cargando</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {busesActivos.sort((a,b) => (a.soc||100)-(b.soc||100)).map(b => (
                <tr key={b.cpId}>
                  <td><span className="badge">{b.cpId}</span></td>
                  <td style={{color:'var(--text2)',fontSize:'12px'}}>{b.cop}</td>
                  <td>
                    {b.zonal !== '—'
                      ? <span style={{color:'var(--accent)',fontWeight:'600',fontSize:'13px'}}>{b.zonal}</span>
                      : <span style={{color:'var(--text4)',fontSize:'12px'}}>Identificando...</span>
                    }
                  </td>
                  <td style={{fontWeight:'700',color:b.soc!=null?socColor(b.soc):'var(--text4)',fontSize:'16px'}}>
                    {b.soc != null ? `${b.soc}%` : '—'}
                  </td>
                  <td style={{width:'120px'}}>
                    {b.soc != null && (
                      <div className="soc-bar-bg">
                        <div className="soc-bar-fill" style={{width:`${b.soc}%`,background:socColor(b.soc),transition:'width 0.5s'}}></div>
                      </div>
                    )}
                  </td>
                  <td style={{color:'#60a5fa',fontWeight:'500'}}>{b.power > 0 ? `${b.power.toFixed(1)} kW` : '—'}</td>
                  <td style={{color:'var(--text2)',fontSize:'12px'}}>{durStr(b.duracion)}</td>
                  <td>
                    <span style={{background:'#1a2a3a',color:'#60a5fa',padding:'2px 8px',borderRadius:'5px',fontSize:'11px',fontWeight:'600'}}>
                      {b.soc != null ? socLabel(b.soc) : 'Cargando'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card" style={{textAlign:'center',padding:'3rem'}}>
          <div style={{fontSize:'40px',marginBottom:'12px'}}>🔌</div>
          <div style={{color:'var(--text2)',fontSize:'16px',fontWeight:'600'}}>Sin buses cargando ahora</div>
          <div style={{color:'var(--text3)',fontSize:'13px',marginTop:'6px'}}>
            {liveChargers.length > 0
              ? `${liveChargers.length} cargadores en vivo esperando buses`
              : 'No hay cargadores conectados al servidor'}
          </div>
        </div>
      )}

      {/* Cargadores disponibles */}
      {disponibles.length > 0 && (
        <div className="card">
          <div className="card-title">Cargadores disponibles ({disponibles.length})</div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
            {disponibles.map(c => (
              <span key={c.id} style={{background:'var(--accent3)',color:'var(--accent)',padding:'4px 12px',borderRadius:'6px',fontSize:'12px',fontWeight:'600',border:'1px solid var(--accent)'}}>
                ✅ {c.id}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
